import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { formatTimeDisplay } from './utils/timeFormat';
import { DAYS, GROUP_ORDER } from '../../constants';
import { byGroupThenOrder } from '../../employees';
import { API_BASE } from './config';


const EmployeeList = ({ employees, setEmployees, selectedStore, stores, onEdit }) => {
    const [showConfirm, setShowConfirm] = useState(false);
    const [employeeToDelete, setEmployeeToDelete] = useState(null);
    const currentStore = stores?.find(s => s._id === selectedStore);

    useEffect(() => {
        if (!selectedStore) return;

        const fetchEmployees = async () => {
            try {
                const response = await axios.get(`${API_BASE}/api/employees`, {
                    params: { location: selectedStore }
                });
                const sortedEmployees = response.data.sort(byGroupThenOrder);
                setEmployees(sortedEmployees);
            } catch (error) {
                console.error('Error fetching employees:', error);
            }
        };
        fetchEmployees();
    }, [selectedStore]);

    const maskRate = (rate) => (rate ? '•'.repeat(String(rate).length) : '');

    const handleEdit = (employee) => {
        onEdit(employee);
    };

    const handleDeleteClick = (employee) => {
        setEmployeeToDelete(employee);
        setShowConfirm(true);
    };

    const confirmDelete = async () => {
        if (!employeeToDelete) return;
        try {
            await axios.delete(`${API_BASE}/api/employees/${employeeToDelete._id}`);
            setEmployees(prev => prev.filter(emp => emp._id !== employeeToDelete._id));
        } catch (error) {
            console.error('Error deleting employee:', error);
        } finally {
            setShowConfirm(false);
            setEmployeeToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowConfirm(false);
        setEmployeeToDelete(null);
    };

    const onDragEnd = async (result) => {
        const { source, destination } = result;
        if (!destination) return;
        if (source.droppableId !== destination.droppableId || source.index === destination.index) return;

        const groupEmployees = employees
            .filter(emp => emp.group === source.droppableId)
            .sort((a, b) => a.order - b.order);

        const reorderedGroup = [...groupEmployees];
        const [removed] = reorderedGroup.splice(source.index, 1);
        reorderedGroup.splice(destination.index, 0, removed);

        reorderedGroup.forEach((emp, index) => {
            emp.order = index;
        });

        const newEmployees = employees.map(emp => {
            if (emp.group === source.droppableId) {
                const reorderedEmp = reorderedGroup.find(e => e._id === emp._id);
                if (reorderedEmp) return { ...emp, order: reorderedEmp.order };
            }
            return emp;
        });

        setEmployees(newEmployees);

        try {
            await axios.put(`${API_BASE}/api/employees/updateOrder`, {
                employees: newEmployees
            });
        } catch (error) {
            console.error('Error updating order:', error);
        }
    };

    return (
        <div className="employee-grid">
            {showConfirm && (
                <div className="modal-overlay">
                    <div className="modal sm">
                        <h3>Confirm Delete</h3>
                        <p>Are you sure you want to delete <strong>{employeeToDelete?.name}</strong>? This cannot be undone</p>
                        <div className="form-section-actions">
                            <button className="cxlbtn" onClick={cancelDelete}>Cancel</button>
                            <button className="btn-confirm" onClick={confirmDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid-header">
                <div></div>
                <div>Name</div>
                <div className="rate">Rate</div>
                <div>Phone</div>
                <div className="divSelect">Group</div>
                {DAYS.map(day => (
                    <div key={day}>{day}</div>
                ))}
                <div>Actions</div>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
                {GROUP_ORDER.map(group => {
                    const groupEmployees = employees
                        .filter(emp => emp.group === group)
                        .sort((a, b) => a.order - b.order);

                    return (
                        <div key={group}>
                            <h5>{group}</h5>
                            <Droppable droppableId={group}>
                                {(provided) => (
                                    <div className="schedule" {...provided.droppableProps} ref={provided.innerRef}>
                                        {groupEmployees.map((emp, index) => (
                                            <Draggable key={emp._id} draggableId={emp._id} index={index}>
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className="employee-item"
                                                    >
                                                        <div>{emp.name}</div>
                                                        <div className="rate line">{maskRate(emp.rate)}</div>
                                                        <div className="line">
                                                            {emp.group === "Manager" || emp.group === "Shift Leader"
                                                            ? (currentStore?.phone || '')
                                                            : emp.phoneNumber}
                                                        </div>
                                                        <div className="line">{emp.group}</div>

                                                        {DAYS.map(day => {
                                                            const availability = emp.availability.find(a => a.day === day);
                                                            const displayText = availability
                                                                ? (availability.startTime === "Not Available" || availability.startTime === "Requested Off"
                                                                    ? formatTimeDisplay(availability.startTime, day)
                                                                    : `${formatTimeDisplay(availability.startTime, day)} - ${formatTimeDisplay(availability.endTime, day) || 'Not Available'}`)
                                                                : 'Not Available';
                                                            return <div className="line" key={day}>{displayText}</div>;
                                                        })}

                                                        <div>
                                                            <button className="editbtn" onClick={() => handleEdit(emp)}>Edit</button>
                                                            <button onClick={() => handleDeleteClick(emp)}>Delete</button>
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </div>
                    );
                })}
            </DragDropContext>
        </div>

    );
};

export default EmployeeList;
